import { useContext, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import AuthClient from "../../api/AuthClient";
import GroupClubRequestsApi from "../../api/GroupClubRequestsApi";
import GroupsClient from "../../api/GroupsClient";
import Header from "../Header";
import Breadcrumbs from "../Breadcrumbs/Breadcrumbs";
import { defaultImgs } from "../../utils/defaultImages";
import { getClubPhoto } from "../../utils/getMedia";
import { OpenClubListing, PartyModel } from "../../types";
import { routes } from "../../routes/routes";
import { getDefaultSelectedOpenClubListingIds } from "./requestClubUtils";
import { PartiesContext } from "../../GlobalContext/partiesContext";
import SortPartyGroupsByDate from "../../SortPartyGroupsByDate";
import RequestClubSkeleton from "./RequestClubSkeleton";
import "../GuestMode/GuestMode.scss";
import { useMyParty, useOpenClubListings } from "../../hooks/useServerDataQueries";
import { useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "../../query/queryKeys";

const emptyOpenClubListings: OpenClubListing[] = [];

const areStringArraysEqual = (first: string[], second: string[]) =>
  first.length === second.length && first.every((value, index) => value === second[index]);

const RequestClub = () => {
  const { id } = useParams<{ id: string }>();
  const authClient = useMemo(() => new AuthClient(), []);
  const { selectedParty, setSelectedPartyContextData, setMyPartiesInfoContextData } = useContext(PartiesContext);
  const queryClient = useQueryClient();
  const contextParty = useMemo(
    () => (selectedParty?.party.id === id ? selectedParty : null),
    [id, selectedParty]
  );
  const { data: queriedParty, isError: isPartyError, isLoading: isPartyLoading } = useMyParty(id);

  const [party, setParty] = useState<PartyModel | null>(contextParty);
  const [selectedListingIds, setSelectedListingIds] = useState<string[]>([]);
  const [clubRequestState, setClubRequestState] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [activeListingId, setActiveListingId] = useState<string | null>(null);
  const [clubRequestError, setClubRequestError] = useState<string>("");
  const {
    data: openClubListings = emptyOpenClubListings,
    isError: isListingsError,
    isLoading: isListingsLoading,
  } = useOpenClubListings(
    party?.party.city,
    party?.party.partyDate,
    party?.party.bookingType
  );

  useEffect(() => {
    const nextParty = queriedParty ?? contextParty;
    if (!nextParty) {
      if (isPartyError) {
        setParty(null);
        setSelectedPartyContextData(null);
      }
      return;
    }

    setParty(nextParty);
    setSelectedPartyContextData(nextParty);
    setMyPartiesInfoContextData((previousParties) =>
      SortPartyGroupsByDate(previousParties.some((currentParty) => currentParty.party.id === nextParty.party.id)
        ? previousParties.map((currentParty) =>
          currentParty.party.id === nextParty.party.id ? nextParty : currentParty
        )
        : [...previousParties, nextParty])
    );
  }, [contextParty, isPartyError, queriedParty, setMyPartiesInfoContextData, setSelectedPartyContextData]);

  useEffect(() => {
    if (!party) {
      return;
    }

    const nextSelectedListingIds = getDefaultSelectedOpenClubListingIds(
      openClubListings,
      party.requestedListings
    );

    setSelectedListingIds((previousSelectedListingIds) =>
      areStringArraysEqual(previousSelectedListingIds, nextSelectedListingIds)
        ? previousSelectedListingIds
        : nextSelectedListingIds
    );
  }, [openClubListings, party]);

  const refreshSelectedParty = async (groupId: string) => {
    const groupsClient = new GroupsClient(authClient);
    const refreshedParty = (await groupsClient.getParsedMyPartyById(groupId))?.data ?? null;

    if (!refreshedParty) {
      throw new Error("Failed to refresh selected party.");
    }

    setParty(refreshedParty);
    queryClient.setQueryData(queryKeys.myParty(groupId), refreshedParty);
    void queryClient.invalidateQueries({ queryKey: queryKeys.myParties() });
    setSelectedPartyContextData(refreshedParty);
    setMyPartiesInfoContextData((previousParties) =>
      SortPartyGroupsByDate(
        previousParties.some((currentParty) => currentParty.party.id === refreshedParty.party.id)
          ? previousParties.map((currentParty) =>
              currentParty.party.id === refreshedParty.party.id ? refreshedParty : currentParty
            )
          : [...previousParties, refreshedParty]
      )
    );
  };

  const toggleSelectedListing = async (listingId: string) => {
    if (!party || clubRequestState === "loading") {
      return;
    }

    const isSelected = selectedListingIds.includes(listingId);
    if (isSelected && selectedListingIds.length === 1) {
      setClubRequestError("At least one club request must remain selected.");
      return;
    }

    setClubRequestError("");
    setClubRequestState("loading");
    setActiveListingId(listingId);

    const groupClubRequestsApi = new GroupClubRequestsApi(authClient);

    try {
      const response = isSelected
        ? await groupClubRequestsApi.removeClubRequest(party.party.id, listingId)
        : await groupClubRequestsApi.addClubRequest(party.party.id, listingId);

      if (!response || response.statusCode >= 400) {
        throw new Error("Failed to update club request.");
      }

      await refreshSelectedParty(party.party.id);
      setClubRequestState("success");
    } catch (error) {
      console.error("Error updating club request", error);
      setClubRequestState("error");
      setClubRequestError("Unable to update club request right now.");
    } finally {
      setTimeout(() => {
        setClubRequestState("idle");
        setActiveListingId(null);
      }, 800);
    }
  };

  const handleClubImageError = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const image = event.currentTarget;
    if (!image.dataset.fallback) {
      image.dataset.fallback = "1";
      image.src = defaultImgs.ClubImg;
    }
  };

  if (isPartyLoading && !party) {
    return <RequestClubSkeleton />;
  }

  if (isPartyError || !party) {
    return (
      <>
        <Header />
        <div className="centerGroup">
          <div className="desktopUI">
            <div className="row userProfile_Header">
              <Breadcrumbs to={id ? routes.MyPartyWithId(id) : routes.MyParties} />
            </div>
            <p className="yellowTxt mt-4">Unable to load club listings for this party.</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="centerGroup">
        <div className="desktopUI">
          <div className="row userProfile_Header">
            <Breadcrumbs to={routes.MyPartyWithId(party.party.id)} />
          </div>

          <div className="sClubs mt-3 d-flex align-items-center w-100">
            <p className="d-flex align-items-center">Requested Clubs</p>
          </div>

          <div className="align-content-center justify-content-start d-flex col">
            <p className="yellowTxt m-2">
              Open club listings for {party.party.city} on this party date are shown below.
            </p>
          </div>
          {clubRequestError ? (
            <p className="yellowTxt m-2" style={{ color: "#ff6b6b" }}>
              {clubRequestError}
            </p>
          ) : null}

          {isListingsLoading ? (
            Array.from({ length: 5 }, (_, index) => (
              <div className="clubCon" key={`request-club-loading-row-${index}`}>
                <div className="form-check m-3 align-items-center col d-flex">
                  <div
                    className="loading-effect"
                    style={{
                      width: "16px",
                      height: "16px",
                      borderRadius: "4px",
                      marginRight: "12px",
                    }}
                  />
                  <div
                    className="loading-effect"
                    style={{
                      width: "50px",
                      height: "35px",
                      marginRight: "12px",
                    }}
                  />
                  <div className="align-content-center col d-flex">
                    <div className="loading-effect loading-effect-text loading-effect-text-lg" />
                  </div>
                </div>
              </div>
            ))
          ) : isListingsError ? (
            <p className="yellowTxt m-2">Unable to load open club listings for this date.</p>
          ) : openClubListings.length === 0 ? (
            <p className="yellowTxt m-2">No open club listings found for this date.</p>
          ) : (
            openClubListings.map((listing) => (
              <div className="clubCon" key={listing.ListingId}>
                <div className="form-check m-3 align-items-center col d-flex">
                  <input
                    className="form-check-input checkC shadow-none"
                    type="checkbox"
                    checked={selectedListingIds.includes(listing.ListingId)}
                    onChange={() => { void toggleSelectedListing(listing.ListingId); }}
                    disabled={clubRequestState === "loading"}
                  />
                  <img
                    src={
                      listing.PictureUrl ??
                      getClubPhoto({
                        clubName: listing.ClubName,
                        cityName: party.party.city,
                      }) ??
                      defaultImgs.ClubImg
                    }
                    alt={listing.DisplayName}
                    className="clubImg"
                    onError={handleClubImageError}
                  />
                  <div className="align-content-center col d-flex">
                    <label className="yellowTxt col text-left">
                      {listing.DisplayName}
                      {activeListingId === listing.ListingId && clubRequestState === "loading" ? (
                        <p className="Req m-0">Updating...</p>
                      ) : null}
                    </label>
                  </div>
                </div>
              </div>
            ))
          )}

          <div className="mb-5" />
        </div>
      </div>
    </>
  );
};

export default RequestClub;
